// One burn-breakdown bar's display strings, and which actions that bar offers. Split out
// of costArtifact.ts for the same reason router-url.ts is split out of router.ts: this is
// pure (no DOM, no state, no fetch), so it can be unit-tested directly, while its home
// module cannot be imported under Node at all — it pulls in sessions.ts, which installs
// document listeners at module load.
//
// Every bar here is one Jira item, and every bar is spend that reached one — spend that
// reached none is out of the population, not a bar (it is the note under the chart). So
// "(unclassified)" is now only a Jira row with no key at all; issuetype rides in the
// SUBTITLE, which is the one place it belongs (it labels a bar well and buckets one
// badly — see Store.costBreakdown's header).

/** A row of /api/cost-breakdown, as much of it as a bar reads. */
export interface BreakdownApiRow {
  id?: string | null
  ident?: string | null
  title?: string | null
  itype?: string | null
  spend?: number
  converted?: number
  wontDo?: number
  drillable?: boolean
  direct?: boolean
}

/** What `breakdownBars` draws: display strings, the three amounts, and the two actions. */
export interface BreakdownBarRow {
  label: string
  sub: string
  spend: number
  converted: number
  wontDo: number
  drillable: boolean
  direct: boolean
  id: string | null
  /** The artifact whose sessions the "Sessions →" action opens; '' = no action. */
  sessId: string
  sessLabel: string
  /** Whether that action rolls up descendants. Per-row — see the comment below. */
  sessSubtree: boolean
}

export function bdRow(r: BreakdownApiRow): BreakdownBarRow {
  let label: string
  let sub = ''
  if (r.direct) {
    // Mirrors the treemap's "(direct work)" tile: money charged to the drilled-in item
    // itself, not to any child. Same fact, same words, hatched the same way in CSS.
    label = '(direct work)'
    sub = 'charged to ' + (r.ident || 'this item') + ' itself, not to a child'
  } else {
    label = r.ident || '(unclassified)'
    sub = r.title || ''
    if (r.itype) sub = sub ? r.itype + ' — ' + sub : r.itype
  }
  // Every TERMINAL bar gets the "Sessions →" action — a bar you cannot drill into is the
  // end of the road, and this is where "what did this money actually get spent doing" has
  // an answer the panel can't show. Deliberately NOT on a drillable bar: that bar already
  // has an action (descend), and a bar with two competing ones is a bar you read twice.
  //
  // Terminal covers two shapes, and each asks for a different session population — which
  // is the whole reason `sessSubtree` is per-row rather than a constant:
  //   * a LEAF item (no children): its subtree is itself, so subtree-or-not is the same
  //     query. Sent as a subtree anyway, matching the Artifacts tab's button exactly, so
  //     the two entry points can't drift.
  //   * the DIRECT-WORK bar: money charged to the breadcrumb item ITSELF, not to a child,
  //     so the honest match is that one item WITHOUT its descendants. Rolling the subtree
  //     up here would answer a wider question than the bar asks.
  const terminal = !r.drillable && !!r.id
  return {
    label,
    sub,
    spend: r.spend ?? 0,
    converted: r.converted ?? 0,
    wontDo: r.wontDo ?? 0,
    drillable: !!r.drillable,
    direct: !!r.direct,
    id: r.id ?? null,
    sessId: terminal ? r.id! : '',
    sessLabel: r.ident || r.id || '',
    sessSubtree: !r.direct,
  }
}
